"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ValidatedField = void 0;
const BasicField_1 = require("./BasicField");
/**
 * A {@link BasicField} that supports validation
 * @class
 * @internal
 */
class ValidatedField extends BasicField_1.BasicField {
    /**
     * Creates a new field instance
     * @param name This field's name (unique value identifier, not label)
     * @param defaultValue Default value to use initially and on reset
     * @param getForm Callback to access the parent form for additional validation context
     * @param validationFunctions Form provided validation functions for this field
     * @param onUpdate Callback to use after updates are made
     */
    constructor(name, defaultValue, getForm, validationFunctions, onUpdate) {
        super(name, defaultValue, onUpdate);
        // internal
        /** ValidationFunctions assigned to this field directly */
        this._localValidationFunctions = [];
        /** Time in ms to wait before validating */
        this._validationDebounce = 0;
        /** Flag to monitor validation state changes, null will check validationDebounce > 0 */
        this._monitorValidation = null;
        /** Tracks the last validated value for caching, @see {@link validate} */
        this._validationCacheValue = undefined;
        /** Tracks the number of validations running */
        this._validationsRunning = 0;
        /** Tracks a {@link setTimeout} timeout for delayed validation calls */
        this._validationTimeout = null;
        /** Internal state to reset when adjusting errors outside normal validation cycle */
        this._resetValidationState = () => {
            // stop any pending validations
            this._cancelPendingValidation();
            // wipe validation cache (could have validated with wiped error, don't want to skip validation)
            this._validationCacheValue = undefined;
        };
        this._getForm = getForm;
        this._formProvidedValidationFunctions = validationFunctions;
    }
    //////////////////////////////////////////////////////////////////////////////
    // overrides
    //////////////////////////////////////////////////////////////////////////////
    /** Clears field errors and reset validation state */
    clearError() {
        this._resetValidationState();
        return super.clearError();
    }
    /** Reset the field and announce update */
    reset() {
        this._resetValidationState();
        super.reset();
    }
    //////////////////////////////////////////////////////////////////////////////
    // getters
    //////////////////////////////////////////////////////////////////////////////
    /** Whether this field is currently validating */
    isValidating() {
        return this._validationsRunning > 0;
    }
    //////////////////////////////////////////////////////////////////////////////
    // setters
    //////////////////////////////////////////////////////////////////////////////
    /**
     * Configures validation options for the field
     *
     * Missing or undefined options are ignored, and will keep default settings
     */
    configureValidation(validationOptions = {}) {
        // validation function(s) provided
        if (validationOptions.validation !== undefined) {
            // store validation functions as an array
            this._localValidationFunctions = !Array.isArray(validationOptions.validation)
                ? [validationOptions.validation]
                : validationOptions.validation;
        }
        // monitorValidation flag provided (true, false, null)
        if (validationOptions.monitorValidation !== undefined) {
            this._monitorValidation = validationOptions.monitorValidation;
        }
        // validation debounce provided
        if (validationOptions.validationDebounce !== undefined) {
            this._validationDebounce = validationOptions.validationDebounce;
        }
    }
    //////////////////////////////////////////////////////////////////////////////
    // validation
    //////////////////////////////////////////////////////////////////////////////
    /** Runs field validation now or later, depending on debounce options */
    queueValidation(useValidationCache = true) {
        const msDelay = this._validationDebounce;
        if (msDelay === 0) {
            // no delay, run immediately
            void this.validate(useValidationCache);
        }
        else {
            // has a delay, set a timeout to validate later
            this._cancelPendingValidation(); // new request/changes made, cancel old queue
            this._validationTimeout = setTimeout(() => {
                void this.validate(useValidationCache);
                this._validationTimeout = null;
            }, msDelay);
        }
    }
    /**
     * Runs validation on the field
     *
     * We track the last validated value, and can (optionally) skip validation if
     * the value has not changed. This is helpful for things like form submission,
     * where we validate all fields, including ones that may have already been
     * validated by an expensive async operation
     *
     * We use the cache on internal methods (standard validation, submission), but
     * will assume end user calls to validate() will always want to actually run
     * the validation
     *
     * @param useValidationCache A flag to use the validation cache for this field
     */
    validate(useValidationCache = false) {
        return __awaiter(this, void 0, void 0, function* () {
            // collect validation functions, preferring locally defined validation first
            const validationFunctions = [...this._localValidationFunctions, ...this._formProvidedValidationFunctions];
            // no validation to run!
            if (validationFunctions.length === 0) {
                // reset any external errors if this field changed
                this.clearError();
                return;
            }
            // get current field value
            const value = this.getValue();
            // we're using cached results, and the value hasn't changed
            if (useValidationCache && value === this._validationCacheValue) {
                return;
            }
            else {
                // otherwise, store this new value
                this._validationCacheValue = value;
            }
            // increment validation counter
            if (this._shouldMonitorValidation()) {
                this._validationsRunning += 1;
                // first validation started
                if (this._validationsRunning === 1) {
                    this._onUpdate();
                }
            }
            // iterate validation functions until we find an error
            let newError = null;
            for (const fn of validationFunctions) {
                const error = yield fn(value, this._getForm());
                if (error) {
                    newError = error;
                    break;
                }
            }
            // set new error and store value used
            if (newError === null) {
                this.clearError();
            }
            else {
                this.setError(newError);
            }
            // decrement validation counter
            if (this._shouldMonitorValidation()) {
                this._validationsRunning -= 1;
                // last validation completed
                if (this._validationsRunning === 0) {
                    this._onUpdate();
                }
            }
        });
    }
    /** Decides if we should monitor validation state changes */
    _shouldMonitorValidation() {
        if (this._monitorValidation === null) {
            return this._validationDebounce > 0;
        }
        return this._monitorValidation;
    }
    /** Clears timeouts for any delayed validation */
    _cancelPendingValidation() {
        if (this._validationTimeout) {
            clearTimeout(this._validationTimeout);
        }
        this._validationTimeout = null;
    }
}
exports.ValidatedField = ValidatedField;
